import React from 'react';

import { dialogService } from '@thefarmersfront/kitchen';

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig, isAxiosError } from 'axios';

import { authDBService } from '~/services';

import { PATH } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';

const API_REQUEST_TIMEOUT = process.env.API_REQUEST_TIMEOUT;
const API_BASE_URL = process.env.API_BASE_URL;

const axiosInstanceService = (() => {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    timeout: Number(API_REQUEST_TIMEOUT),
    paramsSerializer: {
      encode: encodeURIComponent,
    },
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/json`,
    },
  });

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const loginInfo = await authDBService.getLoginInfo();

      const accessToken = loginInfo?.access_token;

      if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  const handleError = async (error: Error | AxiosError) => {
    const isUnAuthorizedError = isAxiosError(error) && error?.response?.status === 401;
    const isLogoutRequest = isAxiosError(error) && error?.config?.url === '/api-owner/user/logout';

    if (isUnAuthorizedError && !isLogoutRequest) {
      dialogService.addDialog({
        title: '토큰 인증 실패',
        children: (
          <>
            {error?.response?.data?.error?.message ?? '토큰 인증에 실패했습니다.'}
            <br />
            {'확인'} 선택 시 로그인 화면으로 이동합니다.
          </>
        ),
        onConfirm: () => {
          if (window.globalNavigate) {
            window.globalNavigate(PATH.LOGOUT);
          } else {
            window.location.href = PATH.LOGOUT;
          }
        },
      });
    }

    if (isAxiosError<TypeErrorResponseData>(error)) {
      const errorResponse = error?.response?.data;

      if (errorResponse instanceof Blob) {
        const errorObj = JSON.parse(await errorResponse.text());
        return errorObj?.error;
      }

      return errorResponse?.error;
    }

    return error;
  };

  return {
    get<T = { data: unknown }, D = unknown>(
      url: string,
      config?: AxiosRequestConfig<D>
    ): Promise<AxiosResponse<T, any>> {
      return instance
        .get<T, AxiosResponse<T>, D>(url, config)
        .then((response) => {
          return response;
        })
        .catch(async (error: Error | AxiosError) => {
          throw await handleError(error);
        });
    },

    post<T = { data: unknown }, D = unknown>(
      url: string,
      data?: D,
      config?: AxiosRequestConfig<D>
    ): Promise<AxiosResponse<T, any>> {
      return instance
        .post<T, AxiosResponse<T>, D>(url, data, config)
        .then((response) => {
          return response;
        })
        .catch(async (error: Error | AxiosError) => {
          throw await handleError(error);
        });
    },

    put<T extends { data: unknown; error?: unknown }, D = unknown>(
      url: string,
      data?: D,
      config?: AxiosRequestConfig<D>
    ): Promise<AxiosResponse<T, any>> {
      return instance
        .put<T, AxiosResponse<T>, D>(url, data, config)
        .then((response) => {
          return response;
        })
        .catch(async (error: Error | AxiosError) => {
          throw await handleError(error);
        });
    },

    delete<T = unknown, D = unknown>(url: string, config?: AxiosRequestConfig<D>): Promise<AxiosResponse<T, any>> {
      return instance
        .delete<T, AxiosResponse<T>, D>(url, config)
        .then((response) => {
          return response;
        })
        .catch(async (error: Error | AxiosError) => {
          throw await handleError(error);
        });
    },

    patch<T extends { data: unknown }, D = unknown>(
      url: string,
      data?: D,
      config?: AxiosRequestConfig<D>
    ): Promise<AxiosResponse<T, any>> {
      return instance
        .patch<T, AxiosResponse<T>, D>(url, data, config)
        .then((response) => {
          return response;
        })
        .catch(async (error: Error | AxiosError) => {
          throw await handleError(error);
        });
    },
  };
})();

export default axiosInstanceService;

const controller = new AbortController();

export const cancelAxiosRequest = () => {
  controller?.abort();
};

export const downloadBlobDataFile = async ({ data: blob, headers }: AxiosResponse) => {
  const contentType = headers['content-type'];
  const contentDisposition = headers['content-disposition'];

  // content-disposition이 attachment가 아닌 경우에만 JSON 에러 체크
  // (서버가 content-type을 application/json으로 잘못 내려줘도 attachment면 파일 다운로드)
  if (!contentDisposition?.trim().startsWith('attachment') && contentType?.includes('application/json')) {
    const text = await (blob as Blob).text();
    const jsonData = JSON.parse(text);

    if (jsonData.error) {
      throw jsonData.error;
    }
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;

  if (contentDisposition) {
    const fileName = decodeURI(contentDisposition.split('filename=')[1] ?? '')?.replaceAll('"', '');

    link.download = fileName;
  }

  link.click();
  link.remove();

  window.URL.revokeObjectURL(url);
};
